import React, { useEffect, useState } from "react";
import { Navbar, NavbarWrapper } from "./navbarStyling";
import { useRecoilState } from "recoil";
import PopupSearchBar from "@/pages/searchListing/components/PopupSearchBar";
import { useLocation, useNavigate } from "react-router-dom";
import { useHomeScroll } from "@/contexts/HomeScrollContext";

// Import navbar components
import HomeButton from "./navbarHomeButton";
import LocationBasedComponents from "./Components/LocationBasedComponents";
import SaveAndExitPopup from "../saveAndExitPopup/saveAndExitPopup";
import CurrencySelectionPopup from "../../currencySelectionPopup/CurrencySelectionPopup";

// Import atom selectors
import { guestModeSelector, userModelSelector } from "@/state/selectors/userModel";
import { mobileSearchMenuActivatedSelector, navbarMenuSelector } from "@/state/selectors/navbarToggle";

import { userHostModelSelector } from "@/state/selectors/userHostModel";

interface NavbarComponentProps {
    showFullPageLoading: React.Dispatch<React.SetStateAction<boolean>>;
    loggingIn: boolean;
}

export default function NavbarComponent({ showFullPageLoading, loggingIn }: NavbarComponentProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const { isHomeScrolled, setIsHomeScrolled } = useHomeScroll();

    const [userModel] = useRecoilState(userModelSelector);
    const [guestMode] = useRecoilState(guestModeSelector);
    const [mobileSearchMenuActivated] = useRecoilState(mobileSearchMenuActivatedSelector);
    const [navbarMenu, setNavbarMenu] = useRecoilState(navbarMenuSelector);
    const [userHostModel] = useRecoilState(userHostModelSelector);
    const [saveAndExitPopup, setSaveAndExitPopup] = useState(false);
    const [notificationsPopup, setNotificationsPopup] = useState(false);
    const [showHomeMiniSearch, setShowHomeMiniSearch] = useState(false);
    const [shouldRenderHomeMini, setShouldRenderHomeMini] = useState(false);
    const [isHomeMiniSearchLeaving, setIsHomeMiniSearchLeaving] = useState(false);
    const [showSearchPopup, setShowSearchPopup] = useState(false);
    const [isCurrencyPopupOpen, setIsCurrencyPopupOpen] = useState(false);

    useEffect(() => {
        if (mobileSearchMenuActivated) {
            document.body.style.overflow = "hidden";
            document.body.style.position = "relative";
            document.body.style.height = "-webkit-fill-available";
            document.documentElement.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
            document.body.style.position = "unset";
            document.body.style.height = "unset";
            document.documentElement.style.overflow = "unset";
        }
    }, [mobileSearchMenuActivated, navbarMenu]);

    // Home page navbar transparency until scrolled
    useEffect(() => {
        if (location.pathname !== "/") {
            setIsHomeScrolled(false);
            setShowHomeMiniSearch(false);
            setIsHomeMiniSearchLeaving(false);
            setShouldRenderHomeMini(false);
            return;
        }
        const handleScroll = () => setIsHomeScrolled(window.scrollY > 0);
        handleScroll();
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [location.pathname]);

    // Keep mini search mounted briefly to allow exit animation (home only)
    useEffect(() => {
        if (location.pathname !== "/") return;
        if (showHomeMiniSearch) {
            setIsHomeMiniSearchLeaving(false);
            setShouldRenderHomeMini(true);
        } else if (shouldRenderHomeMini) {
            setIsHomeMiniSearchLeaving(true);
            const timeoutId = window.setTimeout(() => {
                setShouldRenderHomeMini(false);
                setIsHomeMiniSearchLeaving(false);
            }, 180);
            return () => window.clearTimeout(timeoutId);
        }
    }, [location.pathname, showHomeMiniSearch, shouldRenderHomeMini]);

    // Show mini search on home when the main AI search bar is scrolled past
    useEffect(() => {
        if (location.pathname !== "/") {
            setShowHomeMiniSearch(false);
            return;
        }
        const checkPosition = () => {
            const el = document.getElementById('HOME_PAGE_SEARCH_BAR_ID');
            if (!el) {
                setShowHomeMiniSearch(false);
                return;
            }
            const rect = el.getBoundingClientRect();
            const threshold = window.innerWidth >= 768 ? 80 : 60; // navbar height
            setShowHomeMiniSearch(rect.bottom <= threshold);
        };
        checkPosition();
        window.addEventListener('scroll', checkPosition, { passive: true });
        window.addEventListener('resize', checkPosition);
        return () => {
            window.removeEventListener('scroll', checkPosition);
            window.removeEventListener('resize', checkPosition);
        };
    }, [location.pathname]);

    // Don't show navbar on these pages
    if (location.pathname.includes("/listing") || 
        location.pathname.includes("/booking") || 
        location.pathname.includes("/extras") ||
        location.pathname.includes("/host/profile") ||
        location.pathname.includes("/profile/") ||
        location.pathname.includes("/payments") ||
        location.pathname.includes("/search") ||
        location.pathname.includes("/insights") ||
        location.pathname.includes("/tools") ||
        location.pathname.includes("/s/") ||
        location.pathname.includes("/admin")) {
        return null;
    }

    return (
        <>
            {userModel &&
            !guestMode &&
            !location.pathname.includes("/profile") &&
            !location.pathname.includes("/edit-profile") &&
            !location.pathname.includes("/security-settings") &&
            !location.pathname.includes("/payment-settings") &&
            !location.pathname.includes("/payout-settings") &&
            !location.pathname.includes("/invite") &&
            !location.pathname.includes("/review") ? null : (
                <NavbarWrapper location={location.pathname} navbarMenu={navbarMenu} id="navbar-wrapper">
                    <Navbar
                        location={location.pathname}
                        navbarMenu={navbarMenu}
                        id="navbar"
                        style={location.pathname === "/" ? {
                            background: isHomeScrolled ? "rgba(250, 250, 250, 0.3)" : "transparent",
                            backdropFilter: isHomeScrolled ? "blur(16px)" : "none",
                            transition: "background-color 125ms ease, background 125ms ease, backdrop-filter 125ms ease"
                        } : undefined}
                    >
                        <HomeButton setNavbarMenu={setNavbarMenu} setNavbarSearchMenuActivated={() => {}} isHomeScrolled={isHomeScrolled} />
                        {(() => {
                            const isCollection = location.pathname.includes("/collection");
                            const isHome = location.pathname === "/";
                            const shouldShow = isCollection || (isHome && shouldRenderHomeMini);
                            if (!shouldShow) return null;
                            const animation = isHome
                                ? (showHomeMiniSearch ? 'miniSearchAppear 180ms ease-out' : (isHomeMiniSearchLeaving ? 'miniSearchAppear 180ms ease-in reverse' : undefined))
                                : 'miniSearchAppear 180ms ease-out';
                            return (
                            <>
                                <style>{`
                                    @keyframes miniSearchAppear {
                                        from { opacity: 0; transform: translateY(-6px) scale(0.98); }
                                        to { opacity: 1; transform: translateY(0) scale(1); }
                                    }
                                `}</style>
                                <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                                    <button
                                        onClick={() => setShowSearchPopup(true)}
                                        style={{
                                            width: 440,
                                            height: 52,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 16,
                                            padding: '0 16px',
                                            borderRadius: 100,
                                            border: '1px solid rgba(0,0,0,0.08)',
                                            background: 'rgba(0, 0, 0, 0.08)',
                                            cursor: 'pointer',
                                            // boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                                            animation: animation as any,
                                            transformOrigin: '50% 0%'
                                        }}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 6 6" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
                                            <defs>
                                                <clipPath id="clippath">
                                                    <rect className="cls-3" width="6" height="6" fill="none"/>
                                                </clipPath>
                                            </defs>
                                            <g id="Layer_1-2" data-name="Layer 1">
                                                <g clipPath="url(#clippath)">
                                                    <path fill="#888" fillRule="evenodd" d="M.86,2.57c0-.95,.77-1.71,1.71-1.71s1.71,.77,1.71,1.71-.77,1.71-1.71,1.71-1.71-.77-1.71-1.71ZM2.57,0C1.15,0,0,1.15,0,2.57s1.15,2.57,2.57,2.57c.56,0,1.07-.18,1.49-.48l1.21,1.21c.17,.17,.44,.17,.61,0s.17-.44,0-.61l-1.21-1.21c.3-.42,.48-.93,.48-1.49C5.14,1.15,3.99,0,2.57,0Z"/>
                                                </g>
                                            </g>
                                        </svg>
                                        <span style={{ fontSize: 15.5, color: '#888', whiteSpace: 'nowrap', fontFamily: 'altform-regular' }}>
                                            Search or describe your stay
                                        </span>
                                    </button>
                                </div>
                            </>
                            );
                        })()}
                        <LocationBasedComponents
                            navbarMenu={navbarMenu}
                            setNavbarMenu={setNavbarMenu}
                            setNotificationsPopup={setNotificationsPopup}
                            notificationsPopup={notificationsPopup}
                            setNavbarSearchMenuActivated={() => {}}
                            isCurrencyPopupOpen={isCurrencyPopupOpen}
                            setIsCurrencyPopupOpen={setIsCurrencyPopupOpen}
                            isHomeScrolled={isHomeScrolled}
                        />
                    </Navbar>

                    {saveAndExitPopup && (
                        <SaveAndExitPopup
                            closeSaveAndExitPopup={() => {
                                setSaveAndExitPopup(false);
                                navigate(`/listings/${userHostModel?.id}`);
                            }}
                            activated={saveAndExitPopup}
                        />
                    )}
                </NavbarWrapper>
            )}
            <PopupSearchBar 
                isOpen={showSearchPopup}
                onClose={() => setShowSearchPopup(false)}
                initialValue=""
                isEditingHistory={false}
                parentUserMessageId={undefined}
            />
            {isCurrencyPopupOpen && (
                <CurrencySelectionPopup
                    isOpen={isCurrencyPopupOpen}
                    onClose={() => setIsCurrencyPopupOpen(false)}
                />
            )}
        </>
    );
}
